/**
 * Typen und Hilfen der Seite „Verbraucherschnittstellen" (W2-B2, 16.10.2026).
 *
 * Die Typen folgen `GET …/elektro/verbraucher` (VerbraucherEndpoints.cs,
 * Verbraucherschnittstellenlauf.cs). Gerechnet wird beim Abruf; die Seite schreibt nichts —
 * auch nicht in die Leistungsbilanz.
 */

export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit eigener Dokumentnummer an; sonst liefert er die abgelegte Fassung (F11). '
  + 'Die Excel-Mappe legt keine Fassung an.';

export const ANNAHMEN_HINWEIS =
  'Anlaufart und Anlauffaktor je Verbraucherart sind unbestätigte Planungsannahmen — keine Quelle des Projekts '
  + 'nennt sie (NEA-F8). Der Kabelvorschlag ist ein Mindestquerschnitt nach I_z ohne Häufung, Temperatur und '
  + 'Spannungsfall.';

export const BILANZ_HINWEIS =
  'Die Leistungsbilanz des Modells wird hier NICHT verändert. Die Seite hält die Verbraucher der '
  + 'Fachplaner-Bilanz gegen den Bedarf der Verteiler im Modell und zeigt, was dort fehlt.';

export const ARTEN: Record<string, string> = {
  kueche: 'Küche',
  aufzug: 'Aufzug',
  rolltreppe: 'Fahrtreppe',
  loeschwasser: 'Löschwasser',
  entrauchung: 'Entrauchung',
  pumpe: 'Pumpe',
};

export function artname(kategorie: string): string {
  return ARTEN[kategorie] ?? kategorie;
}

/** Status des Servers als Text. */
export function verbraucherStatusText(status: string): string {
  switch (status) {
    case 'fehler': return 'Fehler';
    case 'warnung': return 'Warnung';
    case 'ohne_abgang': return 'ohne Abgang im Modell';
    case 'ok': return 'Abgang im Modell';
    case 'nicht_im_modell': return 'nicht im Modell';
    case 'ohne_modellbedarf': return 'ohne Bedarf im Modell';
    case 'unterdeckt': return 'unterdeckt';
    case 'gedeckt': return 'gedeckt';
    default: return status;
  }
}

export interface VbKennzahlen {
  verbraucher: number; leistungKw: number; bedarfKw: number; ohneModellabgang: number;
  verteilerNichtImModell: number; svOffen: number; fehlbetragKw: number; befunde: number;
  warnungen: number; fehler: number; aufAnnahmen: boolean;
}

export interface VbQuellzeile {
  quellgruppe: string | null; bezeichnung: string; verteiler: string | null; leistungKw: number | null;
  cosPhi: number | null; gleichzeitigkeit: number | null; stromA: number | null; datei: string; seite: number;
}

export interface VbVerbraucher {
  kategorie: string; bezeichnung: string; kennung: string | null; verteiler: string | null;
  verteilerImModell: boolean; nshv: string | null; quellgruppen: string[]; leistungKw: number; cosPhi: number;
  cosPhiAusQuelle: boolean; gleichzeitigkeit: number | null; bedarfKw: number; bemessungsstromA: number;
  stromQuelleA: number | null; anlaufart: string; anlauffaktor: number; anlaufstromA: number; anlaufKva: number;
  svPflicht: string; svVersorgt: boolean; neaLeistungKw: number | null; neaBefund: string | null;
  modellKreise: number; kabelVorschlag: string | null; kabelIzA: number | null; status: string;
  zeilen: VbQuellzeile[]; befunde: string[];
}

export interface VbVerteiler {
  verteiler: string; imModell: boolean; art: string | null; nshv: string | null; verbraucher: number;
  kategorien: string; verbraucherBedarfKw: number; modellBedarfKw: number | null; modellKreise: number;
  modellKraftanschluesse: number; fehlbetragKw: number | null; status: string;
}

export interface VbNshv {
  nshv: string; verteiler: number; verbraucher: number; verbraucherBedarfKw: number;
  modellBedarfKw: number | null; fehlbetragKw: number;
}

export interface VbKategorie {
  kategorie: string; verbraucher: number; leistungKw: number; bedarfKw: number; imSvBlatt: number;
  ohneVerteiler: number; verteilerImModell: number; modellAbgaenge: number; svPflicht: string; svOffen: number;
}

export interface VbKueche {
  planbereich: string; anschluesse: number; stueck: number; schuko: number; einzel: number; cee: number;
  sonstige: number; mitLeistung: number; leistungKw: number | null;
}

export interface VbPumpe {
  kennung: string | null; familie: string | null; typ: string | null; anlage: string | null;
  leistungKw: number | null; geschoss: string | null; raumnummer: string | null;
}

export interface VbVorgabe {
  feld: string; verwendung: string | null; wert: string; einheit: string | null;
  bestaetigt: boolean; herkunft: string; normbezug: string | null; rueckfall: boolean;
}

export interface VerbraucherStand {
  quelllauf: string | null;
  kennzahlen: VbKennzahlen;
  kategorien: VbKategorie[];
  verbraucher: VbVerbraucher[];
  verteiler: VbVerteiler[];
  nshv: VbNshv[];
  kueche: VbKueche[];
  feuerloeschpumpen: VbPumpe[];
  vorgaben: VbVorgabe[];
  befunde: string[];
  stand: string;
}
